import SDK from 'bookthatapp-sdk';
import axios from 'axios';
import qs from 'qs';
import dayjs from '../../dayjs/dayjs_timezone.js';
import { SHORT_DATE_FORMAT } from '../constants';

const sdk = SDK.init(window.bta.apiToken, { apiBaseUrl: window.bta.apiBaseUrl });
const isUsedSdk = () => !!window.bta.apiToken; // sdk is used for development env (without proxy)
const isVerbose = () => !!window.bta.isDemo;
const proxyBaseUrl = () => (window.bta.proxyBaseUrl);

export const convertFramesToShopTimezone = (frames, timeZone) => {
  // frames returned from server is in UTC
  // we will re-arrange them into shop timezone
  const clientFrames = {};
  let i = 0;

  frames.forEach((f) => {
    f.timeslots.forEach((t) => {
      const timeslotDate = dayjs(t.start)
        .tz(timeZone)
        .format(SHORT_DATE_FORMAT);
      if (!clientFrames[timeslotDate]) {
        clientFrames[timeslotDate] = {
          id: i,
          year: dayjs(t.start)
            .tz(timeZone)
            .format('YYYY'),
          month: dayjs(t.start)
            .tz(timeZone)
            .format('MM'),
          date: dayjs(t.start)
            .tz(timeZone)
            .format(SHORT_DATE_FORMAT),
          timeslots: [],
        };
        i++;
      }

      clientFrames[timeslotDate].timeslots.push({
        ...t,
        date: dayjs(t.start)
          .tz(timeZone)
          .format(SHORT_DATE_FORMAT),
        start: t.start,
        finish: t.finish,
      });
    });
  });

  return Object.values(clientFrames);
};

export const flattenFramesToShopTimezone = (frames, timeZone) => {
  const arr = [];

  frames.forEach((frame) => {
    frame.timeslots.forEach((timeslot) => {
      arr.push({
        ...timeslot,
        startStr: dayjs(timeslot.start).format('YYYY-MM-DD HH:mm:ss Z'),
        start: dayjs(timeslot.start)
          .tz(timeZone)
          .toDate(),
        finishStr: dayjs(timeslot.finish).format('YYYY-MM-DD HH:mm:ss Z'),
        finish: dayjs(timeslot.finish)
          .tz(timeZone)
          .toDate(),
      });
    });
  });

  return arr;
};

const buildFrameData = (data) => {
  const { timeZone } = window.bta;

  return {
    ...data,
    frames: convertFramesToShopTimezone(data.frames, timeZone),
    flattenTimeSlots: flattenFramesToShopTimezone(data.frames, timeZone),
    lastOccurrence: data.last_occurrence
      ? dayjs(data.last_occurrence)
        .tz(timeZone)
        .format(SHORT_DATE_FORMAT)
      : null,
    nextOccurrence: data.next_occurrence
      ? dayjs(data.next_occurrence)
        .tz(timeZone)
        .format(SHORT_DATE_FORMAT)
      : null,
  };
};

export const getFrames = ({
  external_id, start, finish, location_id, resource_id, is_events_list,
}) => {
  const params = {
    external_id,
    start,
    finish,
    location_id,
    resource_id,
    is_events_list,
  };

  if (isUsedSdk()) {
    return sdk.getFrames(params)
      .then(({ data }) => Promise.resolve(buildFrameData(data)));
  }
  return axios.get(`${proxyBaseUrl()}/api/v1/frames`, {
    params: { ...params, shop: window.bta ? window.bta.shop : null },
    headers: { 'x-btaapi-verbose': isVerbose() },
  }).then(({ data }) => Promise.resolve(buildFrameData(data)));
};

export const getCourseFrames = ({
  external_id, start, finish, location_id, resource_id,
}) => {
  const params = {
    external_id,
    start,
    finish,
    location_id,
    resource_id,
  };

  if (isUsedSdk()) {
    return sdk.getCourseFrames(params)
      .then(({ data }) => Promise.resolve(buildFrameData(data)));
  }
  return axios.get(`${proxyBaseUrl()}/api/v1/courses`, {
    params: { ...params, shop: window.bta ? window.bta.shop : null },
    headers: { 'x-btaapi-verbose': isVerbose() },
  }).then(({ data }) => Promise.resolve(buildFrameData(data)));
};

export const getBlocks = ({
  // eslint-disable-next-line camelcase
  external_id, interval, finish, location_ids, resource_ids, start, with_finish_availability,
}) => {
  const params = {
    external_id,
    interval,
    start: dayjs(start).format(SHORT_DATE_FORMAT),
    finish: dayjs(finish).format(SHORT_DATE_FORMAT),
    location_ids,
    resource_ids,
    with_finish_availability,
  };

  if (isUsedSdk()) {
    return sdk
      .getBlocks(params)
      .then(({ data }) => Promise.resolve({ ...data }));
  }
  return axios.get(`${proxyBaseUrl()}/api/v1/blocks`, {
    params: { ...params, shop: window.bta ? window.bta.shop : null },
    headers: { 'x-btaapi-verbose': isVerbose() },
  }).then(({ data }) => Promise.resolve({ ...data }));
};

export const createBooking = (bookingParam) => {
  const params = {
    cart_token: window.xprops.cartToken,
    ...bookingParam,
  };

  if (isUsedSdk()) {
    return sdk.createBooking(params);
  }
  return axios.post(
    `${proxyBaseUrl()}/api/v1/bookings`,
    qs.stringify({ ...params, shop: window.bta ? window.bta.shop : null }),
    { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
  );
};

export const rescheduleBooking = (bookingToken, params) => axios.post(
  `${proxyBaseUrl()}/bookings/reschedule/${bookingToken}`,
  qs.stringify({ ...params, shop: window.bta ? window.bta.shop : null }),
  { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
);

export const createReservation = (items, check_waitlist, retry, check_expires) => {
  const params = {
    cart_token: window.xprops.cartToken,
    items,
    retry,
    check_waitlist,
    check_expires,
  };

  if (isUsedSdk()) {
    return sdk.createReservation(params);
  }
  return axios.post(
    `${proxyBaseUrl()}/api/v1/reservations`,
    qs.stringify({ ...params, shop: window.bta ? window.bta.shop : null }),
    { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
  );
};

export const confirmAvailability = (items, check_waitlist, retry, check_expires) => {
  const params = {
    cart_token: window.xprops.cartToken,
    items,
    retry,
    check_waitlist,
    check_expires,
  };

  if (isUsedSdk()) {
    return sdk.createReservation(params);
  }
  return axios.post(
    `${proxyBaseUrl()}/api/v1/confirm_availability`,
    qs.stringify({ ...params, shop: window.bta ? window.bta.shop : null }),
    { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
  );
};

export const addToWaitlist = (productId, params) => axios.post(
  `${proxyBaseUrl()}/api/v1/products/${productId}/waitlist`,
  qs.stringify({ ...params, shop: window.bta ? window.bta.shop : null }),
  { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
);

export const availabilitySearch = (params) => axios.get(
  `${proxyBaseUrl()}/api/v1/products/search`,
  { params: { options: { ...params }, shop: window.bta ? window.bta.shop : null } },
)
  .then(({ data }) => Promise.resolve({ ...data }));

export const getLocations = (params) => axios.get(
  `${proxyBaseUrl()}/api/v1/locations`,
  { params: { ...params, shop: window.bta ? window.bta.shop : null } },
)
  .then(({ data }) => Promise.resolve({ ...data }));

export const getWidgetProducts = (widgetId) => axios.get(
  `${proxyBaseUrl()}/widgets/${widgetId}/products`,
  { params: { shop: window.bta ? window.bta.shop : null } },
);

export const getProducts = (customParams) => {
  if (isUsedSdk()) {
    return sdk.getProducts()
      .then((response) => response.data)
      .catch((error) => { throw error; });
  }
  const params = {
    ...customParams,
    shop: window.bta ? window.bta.shop : null,
  };

  return axios.get(`${proxyBaseUrl()}/api/v1/products`, { params })
    .then((response) => response.data)
    .catch((error) => { throw error; });
};

export const getSchedules = (params) => {
  if (window.bta && window.bta.appUrl) {
    const { appUrl } = window.bta;
    const apiUrl = `${appUrl}/availability/schedule.json`;
    return axios.get(apiUrl, { params })
      .then((resp) => (resp.data))
      .catch((error) => {
        throw error;
      });
  }
  return Promise.reject(new Error('getSchedules - no window.xprops...'));
};

export const getCalendarEvents = (params) => {
  if (window.bta && window.bta.appUrl) {
    const { appUrl } = window.bta;
    const apiUrl = `${appUrl}/calendar/events.json`;
    return axios.get(apiUrl, { params })
      .then((resp) => (resp.data))
      .catch((error) => {
        throw error;
      });
  }
  return Promise.reject(new Error('getCalendarEvents - no window.xprops...'));
};

export const getUpcomingEventsProducts = (options) => {
  const apiUrl = `${proxyBaseUrl()}/upcoming_events_products`;
  const { showBookedOut, showRepeatOccurrences } = options || {};

  return axios.get(apiUrl, {
    params: {
      start: dayjs().utc()
        .format('YYYY-MM-DDTHH:mm'),
      finish: dayjs().utc()
        .add(5, 'month')
        .format('YYYY-MM-DDTHH:mm'),
      shop: window.bta ? window.bta.shop : null,
      show_booked_out: showBookedOut || false,
      show_repeat_occurrences: showRepeatOccurrences || false,
    },
  })
    .then((resp) => (resp.data))
    .catch((error) => {
      throw error;
    });
};

export default {
  availabilitySearch,
  createReservation,
  getBlocks,
  getCourseFrames,
  getFrames,
  getProducts,
  getSchedules,
};
